import {
  a,
  div,
  h1,
  h2,
  label,
  p,
  span,
  input,
  button,
  select,
  option,
  fnstate,
  fnlink,
  goTo
} from '@srfnstack/fntags'
import { NumberSpinner } from '../components/NumberSpinner.js'
import { ALL_IMAGE_PROVIDERS } from '../components/ModelSelector.js'
import { extensionId, user } from '../state.js'

export const Settings = () => {
  const settings = fnstate({
    launch_mode: true,
    daily_publish_limit: 5,
    image_gen_batch_size: 5,
    image_gen_model: 'bfl_flux2pro',
    pricing_strategy: 'TARGET_PROFIT',
    target_profit: 1000,
    target_margin: 0.40,
    // Lower margin for large sizes; both null = no tier (see settings.rt.js).
    large_size_margin: null,
    large_size_min_side_in: null,
    // The permanent Etsy sale behind the list price, and the sale the operator
    // last created by hand in Shop Manager (ETSY_SALE_RENEW reminds from it).
    etsy_sale_percent: 0,
    etsy_sale_name: null,
    etsy_sale_starts_at: null,
    etsy_sale_ends_at: null,
    etsy_sale_scope: null,
    // Pinterest Pin Queue: draft pins daily for review, publish the approved.
    pinterest_autopin_enabled: false,
    pinterest_daily_pins: 8,
    // Auto-approve: drafts the critic passes publish at their slot (Reject still works).
    pinterest_auto_approve: false,
    // Content layer: AI copy plus the critic, and fresh pin images for re-pins.
    pinterest_ai_copy: true,
    pinterest_creatives: true,
    // Learning loop: the learned selector runs in shadow until this is on;
    // board proposals (operator-approved) are on by default.
    pinterest_learned_selection: false,
    pinterest_board_explore_enabled: true,
    // Taste challenger: AI appeals for rejected artwork run in shadow until on.
    taste_challenger_enabled: false
  })
  // Like autopilotLoaded: the pin queue switch is not saveable until the real
  // values have landed, so a fast click cannot write the defaults over them.
  const settingsLoaded = fnstate(false)
  const savingPinQueue = fnstate(false)
  const printify = fnstate({ shopId: '', accessToken: '', hasToken: false, hasSessionToken: false, sessionExpiresAt: null, health: 'OK', shops: [] })
  const saving = fnstate(false)
  const savingPrintify = fnstate(false)
  const discord = fnstate({ webhookUrl: '', hasWebhook: false })
  const savingDiscord = fnstate(false)
  const etsy = fnstate({ appConfigured: false, hasOAuth: false, shopId: '' })
  const savingEtsy = fnstate(false)
  const etsyApp = fnstate({ keystring: '', sharedSecret: '', hasSecret: false })
  const savingEtsyApp = fnstate(false)
  const pinterest = fnstate({ appConfigured: false, hasOAuth: false, username: '' })
  const savingPinterest = fnstate(false)
  const pinterestApp = fnstate({ clientId: '', clientSecret: '', hasSecret: false })
  const savingPinterestApp = fnstate(false)
  const bflApp = fnstate({ lowCreditThreshold: 0, credits: null })
  const savingBflApp = fnstate(false)
  const autopilot = fnstate({ autopilot_enabled: false, approval_mode: 'one_click', target_listings_per_day: 4, tick_interval_minutes: 60, max_weekly_credits: 20, tombstone_enabled: true, tombstone_min_shop_items: 500, tombstone_age_days: 90, tombstone_sale_ttl_days: 365, require_artwork_approval: true, tribe_engagement_weighting: true, gen_explore_floor: false, gen_explore_share: 0.2, gen_novelty_slot: false, gen_winner_seeds: false, gen_pinterest_weights: false, gen_trend_seeds: false, gen_thompson_weights: false, gen_commercial_share: 0, gen_triptych: false, gen_triptych_share: 0.1 })
  const savingAutopilot = fnstate(false)
  // The page renders synchronously against the defaults above while fetchSettings
  // is still in flight. Saving in that window PUTs the whole defaults object, which
  // silently writes autopilot_enabled: false over a live shop. Nothing is saveable
  // until the real values have landed.
  const autopilotLoaded = fnstate(false)
  const productTypes = fnstate([])
  const loadingProductTypes = fnstate(true)
  const blueprints = fnstate([])
  const loadingBlueprints = fnstate(true)

  const fetchShops = async () => {
    try {
      const res = await fetch('/api/system/printify/shops')
      if (res.ok) {
        const shops = await res.json()
        printify.assign({ shops })
        if (shops.length > 0 && !printify().shopId) {
          // Suggest a shop with a live sales channel, disconnected shops linger in
          // the account list and pushing to one fails.
          const preferred = shops.find(s => s.sales_channel && s.sales_channel !== 'disconnected') || shops[0]
          printify.assign({ shopId: preferred.id.toString() })
        }
      }
    } catch (err) {
      console.error('Error fetching shops:', err)
    }
  }

  const fetchSettings = async () => {
    // Autopilot first: it used to sit behind the Printify shop lookup (a live
    // Printify API round trip), leaving the master switch rendering OFF for
    // seconds after the page was interactive.
    const apRes = await fetch('/api/system/autopilot/settings')
    if (apRes.ok) {
      autopilot(await apRes.json())
      autopilotLoaded(true)
    }
    const res = await fetch('/api/system/settings')
    if (res.ok) {
      settings({ ...settings(), ...(await res.json()) })
      settingsLoaded(true)
    }
    const pRes = await fetch('/api/system/integrations/printify')
    if (pRes.ok) {
      const data = await pRes.json()
      printify.assign({ shopId: data.shopId, hasToken: data.hasToken, hasSessionToken: data.hasSessionToken || false, sessionExpiresAt: data.sessionExpiresAt || null, health: data.health || 'OK' })
      if (data.hasToken) {
        await fetchShops()
      }
    }
    const dRes = await fetch('/api/system/integrations/discord')
    if (dRes.ok) {
      const data = await dRes.json()
      discord.assign({ hasWebhook: data.hasWebhook })
    }
    // After the settings, so the checkboxes render against the saved pool.
    const ptRes = await fetch('/api/system/autopilot/product-types')
    const ptData = ptRes.ok ? await ptRes.json().catch(() => []) : []
    productTypes(Array.isArray(ptData) ? ptData : [])
    loadingProductTypes(false)
    const bpRes = await fetch('/api/system/autopilot/blueprint-defaults')
    const bpData = bpRes.ok ? await bpRes.json().catch(() => []) : []
    blueprints(Array.isArray(bpData) ? bpData : [])
    loadingBlueprints(false)
    const etsyRes = await fetch('/api/system/integrations/etsy')
    if (etsyRes.ok) {
      const d = await etsyRes.json()
      etsy.assign({ appConfigured: !!d.appConfigured, hasOAuth: !!d.hasOAuth, shopId: d.shopId || '' })
    }
    const pinRes = await fetch('/api/system/integrations/pinterest')
    if (pinRes.ok) {
      const d = await pinRes.json()
      pinterest.assign({ appConfigured: !!d.appConfigured, hasOAuth: !!d.hasOAuth, username: d.username || '' })
    }
    const u = user()
    if (u && u.tenant_id === 1 && u.role === 'SUPER_ADMIN') {
      const appRes = await fetch('/api/system/app-settings/etsy')
      if (appRes.ok) {
        const d = await appRes.json()
        etsyApp.assign({ keystring: d.keystring || '', hasSecret: !!d.hasSecret })
      }
      const bflRes = await fetch('/api/system/app-settings/bfl')
      if (bflRes.ok) {
        const d = await bflRes.json()
        bflApp.assign({ lowCreditThreshold: Number(d.lowCreditThreshold) || 0, credits: d.credits })
      }
      const pinAppRes = await fetch('/api/system/app-settings/pinterest')
      if (pinAppRes.ok) {
        const d = await pinAppRes.json()
        pinterestApp.assign({ clientId: d.clientId || '', hasSecret: !!d.hasSecret })
      }
    }
  }

  const connectEtsy = async () => {
    savingEtsy(true)
    try {
      const res = await fetch('/api/system/integrations/etsy-connect')
      if (res.ok) {
        const { authUrl } = await res.json()
        window.location = authUrl
      } else {
        const e = await res.json().catch(() => ({}))
        await window.alert(`Could not start Etsy connect: ${e.error || 'error'}`)
      }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
    savingEtsy(false)
  }

  // Super-admin: save the app-level Etsy credentials (shared by all tenants).
  const saveEtsyApp = async () => {
    savingEtsyApp(true)
    try {
      const res = await fetch('/api/system/app-settings/etsy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keystring: etsyApp().keystring, shared_secret: etsyApp().sharedSecret })
      })
      if (res.ok) {
        etsyApp.assign({ sharedSecret: '', hasSecret: etsyApp().hasSecret || !!etsyApp().sharedSecret })
        await window.alert('Etsy app credentials saved.')
      } else {
        const e = await res.json().catch(() => ({}))
        await window.alert(`Failed: ${e.error || 'error'}`)
      }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
    savingEtsyApp(false)
  }

  const connectPinterest = async () => {
    savingPinterest(true)
    try {
      const res = await fetch('/api/system/integrations/pinterest-connect')
      if (res.ok) {
        const { authUrl } = await res.json()
        window.location = authUrl
      } else {
        const e = await res.json().catch(() => ({}))
        await window.alert(`Could not start Pinterest connect: ${e.error || 'error'}`)
      }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
    savingPinterest(false)
  }

  const disconnectPinterest = async () => {
    savingPinterest(true)
    try {
      const res = await fetch('/api/system/integrations/pinterest-disconnect', { method: 'POST' })
      if (res.ok) {
        pinterest.assign({ hasOAuth: false, username: '' })
      } else {
        const e = await res.json().catch(() => ({}))
        await window.alert(`Failed: ${e.error || 'error'}`)
      }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
    savingPinterest(false)
  }

  // Super-admin: save the app-level Pinterest credentials (shared by all tenants).
  const savePinterestApp = async () => {
    savingPinterestApp(true)
    try {
      const res = await fetch('/api/system/app-settings/pinterest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: pinterestApp().clientId, client_secret: pinterestApp().clientSecret })
      })
      if (res.ok) {
        pinterestApp.assign({ clientSecret: '', hasSecret: pinterestApp().hasSecret || !!pinterestApp().clientSecret })
        await window.alert('Pinterest app credentials saved.')
      } else {
        const e = await res.json().catch(() => ({}))
        await window.alert(`Failed: ${e.error || 'error'}`)
      }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
    savingPinterestApp(false)
  }

  // Super-admin: save the app-level BFL low-credit alert threshold.
  const saveBflApp = async () => {
    savingBflApp(true)
    try {
      const res = await fetch('/api/system/app-settings/bfl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lowCreditThreshold: Number(bflApp().lowCreditThreshold) || 0 })
      })
      if (res.ok) {
        await window.alert('BFL low-credit threshold saved.')
      } else {
        const e = await res.json().catch(() => ({}))
        await window.alert(`Failed: ${e.error || 'error'}`)
      }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
    savingBflApp(false)
  }

  // One editable row per product type. Inputs are uncontrolled (read on save) so
  // the list never re-renders under the user's cursor.
  const blueprintRow = (row) => {
    const sizesInput = input({
      type: 'text',
      class: 'input input-bordered input-sm w-full',
      placeholder: 'e.g. 12x12, 16x20 (blank = all)',
      value: (row.variant_filter.include_sizes || []).join(', ')
    })
    const maxInput = input({ type: 'number', class: 'input input-bordered input-sm w-full', min: 0, value: row.variant_filter.max_variants || '' })
    const marginInput = input({ type: 'number', class: 'input input-bordered input-sm w-full', min: 0, max: 95, placeholder: 'default', value: row.pricing.target_margin ? Math.round(row.pricing.target_margin * 100) : '' })
    const savingRow = fnstate(false)
    const save = async () => {
      savingRow(true)
      const sizes = sizesInput.value.split(',').map(s => s.trim()).filter(Boolean)
      const maxV = parseInt(maxInput.value, 10) || 0
      const marginPct = parseFloat(marginInput.value)
      const pricing = (!isNaN(marginPct) && marginPct > 0) ? { strategy: 'TARGET_MARGIN', target_margin: marginPct / 100 } : {}
      try {
        const res = await fetch('/api/system/autopilot/blueprint-defaults', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_type: row.product_type, variant_filter: { include_sizes: sizes, max_variants: maxV }, pricing })
        })
        if (res.ok) await window.alert(`Saved ${row.product_type}.`)
        else { const e = await res.json().catch(() => ({})); await window.alert(`Failed: ${e.error || 'error'}`) }
      } catch (err) { await window.alert(`Failed: ${err.message}`) }
      savingRow(false)
    }
    return div({ class: 'border border-base-300 rounded p-3 space-y-2' },
      div({ class: 'font-semibold text-sm capitalize' }, row.product_type),
      div({ class: 'grid grid-cols-1 sm:grid-cols-3 gap-2' },
        div({}, label({ class: 'label py-0' }, span({ class: 'label-text text-xs' }, 'Sizes')), sizesInput),
        div({}, label({ class: 'label py-0' }, span({ class: 'label-text text-xs' }, 'Max variants')), maxInput),
        div({}, label({ class: 'label py-0' }, span({ class: 'label-text text-xs' }, 'Margin %')), marginInput)
      ),
      div({ class: 'flex justify-end' },
        button({ class: 'btn btn-xs btn-primary', disabled: savingRow.bindAttr(s => s), onclick: save },
          savingRow.bindAs(s => s ? 'Saving…' : 'Save'))
      )
    )
  }

  // Enabled product types are stored as a list, not a flag per row, so the guard
  // lives here: unchecking the last one would be saved as "generate nothing",
  // which the job engine reads as "unconfigured" and answers with every default
  // type back on. Refuse it at the checkbox instead of failing at Save.
  const toggleProductType = async (keyword, checked, el) => {
    const current = autopilot().default_product_types || []
    const next = checked ? [...new Set([...current, keyword])] : current.filter(t => t !== keyword)
    if (next.length === 0) {
      el.checked = true
      await window.alert('At least one product type has to stay on. Turn Autopilot off if you want it to stop generating entirely.')
      return
    }
    autopilot.assign({ default_product_types: next })
  }

  const productTypeRow = (rowState) => {
    const r = rowState()
    const enabled = (autopilot().default_product_types || []).includes(r.product_type)
    const perListing = r.published > 0
      ? `${r.views_per_listing} views · ${r.favorites_per_listing} favs · $${r.revenue_per_listing} per listing`
      : 'no published listings yet'
    return div({ class: 'flex items-center justify-between gap-3 border border-base-300 rounded p-2' },
      label({ class: 'label cursor-pointer justify-start gap-3 py-0' },
        input({
          type: 'checkbox',
          class: 'toggle toggle-primary toggle-sm',
          checked: enabled,
          onchange: (e) => toggleProductType(r.product_type, e.target.checked, e.target)
        }),
        span({ class: 'label-text font-semibold capitalize' }, r.product_type)
      ),
      div({ class: 'text-xs opacity-70 text-right leading-tight' },
        div(`${r.published} published · ${r.sellers} sold · $${r.revenue.toFixed(2)}`),
        div({ class: 'opacity-80' }, perListing)
      )
    )
  }

  const saveAutopilot = async () => {
    // The PUT sends every field, so an unloaded state is not a partial save, it is
    // a full overwrite with defaults. Refuse rather than write fiction.
    if (!autopilotLoaded()) {
      await window.alert('Autopilot settings are still loading, try again in a moment.')
      return
    }
    if (savingAutopilot()) return
    savingAutopilot(true)
    try {
      const res = await fetch('/api/system/autopilot/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(autopilot())
      })
      if (res.ok) {
        const data = await res.json()
        if (data.settings) autopilot(data.settings)
        await window.alert('Autopilot settings saved.')
      } else {
        const err = await res.json().catch(() => ({}))
        await window.alert(`Failed to save autopilot settings: ${err.error || 'Unknown error'}`)
      }
    } catch (err) {
      await window.alert(`Failed to save autopilot settings: ${err.message}`)
    }
    savingAutopilot(false)
  }

  const saveDiscord = async () => {
    const url = discord().webhookUrl.trim()
    if (!url) return
    savingDiscord(true)
    try {
      const res = await fetch('/api/system/integrations/discord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhookUrl: url })
      })
      if (res.ok) {
        discord.assign({ hasWebhook: true, webhookUrl: '' })
        await window.alert('Discord webhook saved. Use "Send Test Notification" to verify the channel.')
      } else {
        const err = await res.json().catch(() => ({}))
        await window.alert(`Failed to save Discord webhook: ${err.error || 'Unknown error'}`)
      }
    } catch (err) {
      await window.alert(`Failed to save Discord webhook: ${err.message}`)
    }
    savingDiscord(false)
  }

  const sendTestDiscord = async () => {
    savingDiscord(true)
    try {
      const res = await fetch('/api/system/integrations/discord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ testOnly: true })
      })
      if (res.ok) {
        await window.alert('Test notification sent, check your Discord channel.')
      } else {
        const err = await res.json().catch(() => ({}))
        await window.alert(`Could not send test: ${err.error || 'Save a webhook first.'}`)
      }
    } catch (err) {
      await window.alert(`Could not send test: ${err.message}`)
    }
    savingDiscord(false)
  }

  const saveSettings = async () => {
    saving(true)
    const res = await fetch('/api/system/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings())
    })
    if (res.ok) {
      await window.alert('Settings updated successfully!')
    } else {
      await window.alert('Failed to update settings.')
    }
    saving(false)
  }

  // Saves only the Pin Queue fields, so it never writes the rest of the
  // settings form.
  const savePinQueue = async () => {
    if (!settingsLoaded()) return
    savingPinQueue(true)
    const s = settings()
    const res = await fetch('/api/system/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pinterest_autopin_enabled: s.pinterest_autopin_enabled,
        pinterest_daily_pins: s.pinterest_daily_pins,
        pinterest_auto_approve: !!s.pinterest_auto_approve,
        pinterest_ai_copy: s.pinterest_ai_copy !== false,
        pinterest_creatives: s.pinterest_creatives !== false,
        pinterest_learned_selection: !!s.pinterest_learned_selection,
        pinterest_board_explore_enabled: s.pinterest_board_explore_enabled !== false,
        taste_challenger_enabled: !!s.taste_challenger_enabled
      })
    })
    if (res.ok) {
      await window.alert('Pin Queue settings saved.')
    } else {
      const e = await res.json().catch(() => ({}))
      await window.alert(`Failed to save Pin Queue settings: ${e.error || res.status}`)
    }
    savingPinQueue(false)
  }

  const savePrintify = async (silent = false) => {
    if (!silent) savingPrintify(true)
    const res = await fetch('/api/system/integrations/printify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shopId: printify().shopId,
        accessToken: printify().accessToken
      })
    })
    if (res.ok) {
      const hasToken = printify().hasToken || !!printify().accessToken
      printify.assign({ hasToken })
      if (!silent) {
        printify.assign({ accessToken: '' })
        await window.alert('Printify integration updated!')
      }
      await fetchShops()
    } else if (!silent) {
      await window.alert('Failed to update Printify integration.')
    }
    if (!silent) savingPrintify(false)
  }

  let debounceTimer
  const handleTokenInput = (e) => {
    const accessToken = e.target.value
    printify.assign({ accessToken })
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      savePrintify(true)
    }, 100)
  }

  // Human-readable Printify session state for the login section.
  const sessionStatus = (p) => {
    if (!p.hasSessionToken) return { text: 'Not connected yet, sign in to enable publishing.', cls: 'opacity-80' }
    if (p.health === 'NEEDS_REAUTH') return { text: '⚠ Session needs reconnecting, autopilot is paused until you sign in again.', cls: 'text-warning font-semibold' }
    if (!p.sessionExpiresAt) return { text: '✓ Connected: session credentials captured.', cls: 'text-success font-semibold' }
    const ms = new Date(p.sessionExpiresAt).getTime() - Date.now()
    if (isNaN(ms) || ms <= 0) return { text: '⚠ Session expired, reconnect to resume autopilot.', cls: 'text-warning font-semibold' }
    const hrs = Math.floor(ms / 3600000)
    const mins = Math.floor((ms % 3600000) / 60000)
    const rem = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`
    return { text: `✓ Connected: session valid for about ${rem}.`, cls: 'text-success font-semibold' }
  }

  fetchSettings()

  return div({ class: 'p-6' },
    h1({ class: 'text-3xl font-bold mb-6' }, 'Settings'),
    div({ class: 'max-w-2xl space-y-6' },
      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-4' }, 'Business Logic'),
        div({ class: 'space-y-4' },
          div({ class: 'form-control' },
            label({ class: 'label cursor-pointer' },
              span({ class: 'label-text' }, 'Launch Mode ($2.00 Target Profit)'),
              input({
                type: 'checkbox',
                class: 'toggle toggle-primary',
                checked: settings.bindAttr(s => s.launch_mode),
                onchange: (e) => settings.assign({ launch_mode: e.target.checked })
              })
            )
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Daily Publish Limit')),
            NumberSpinner({
              state: wrapBindAttr(settings, 'daily_publish_limit'),
              min: 1,
              max: 100
            })
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Image Gen Batch Size')),
            NumberSpinner({
              state: wrapBindAttr(settings, 'image_gen_batch_size'),
              min: 1,
              max: 100
            })
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Default Image Generation Model')),
            select({
              class: 'select select-bordered select-sm w-full',
              onchange: (e) => settings.assign({ image_gen_model: e.target.value })
            },
            ALL_IMAGE_PROVIDERS.map(p =>
              option({
                value: p.value,
                selected: settings.bindAttr(s => s.image_gen_model === p.value)
              }, p.label)
            ))
          )
        ),
        div({ class: 'card-actions justify-end mt-6' },
          button({
            class: 'btn btn-primary',
            disabled: saving.bindAttr(s => s),
            onclick: saveSettings
          },
          saving.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save Settings')
          )
        )
      ),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-1' }, 'Autopilot'),
        p({ class: 'text-sm opacity-70 mb-4' },
          'Fully autonomous mode. EtherBuildr discovers ideas, generates and quality-checks artwork + mockups, pushes to Printify, and fills the Approval Queue, on a cadence, with no clicks.'),
        div({ class: 'space-y-4' },
          div({ class: 'form-control' },
            label({ class: 'label cursor-pointer' },
              span({ class: 'label-text font-semibold' }, 'Enable Autopilot'),
              input({
                type: 'checkbox',
                class: 'toggle toggle-primary',
                checked: autopilot.bindAttr(a => a.autopilot_enabled),
                onchange: (e) => autopilot.assign({ autopilot_enabled: e.target.checked })
              })
            )
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Approval Mode')),
            select({
              class: 'select select-bordered select-sm w-full',
              onchange: (e) => autopilot.assign({ approval_mode: e.target.value })
            },
            option({ value: 'one_click', selected: autopilot.bindAttr(a => a.approval_mode === 'one_click') }, 'One-click: review in the Approval Queue before publishing (recommended)'),
            option({ value: 'full_auto', selected: autopilot.bindAttr(a => a.approval_mode === 'full_auto') }, 'Full auto: publish to Etsy automatically when quality is high')
            )
          ),
          div({ class: 'form-control' },
            label({ class: 'label cursor-pointer justify-start gap-3' },
              input({
                type: 'checkbox',
                class: 'toggle toggle-primary',
                checked: autopilot.bindAttr(a => a.require_artwork_approval),
                onchange: (e) => autopilot.assign({ require_artwork_approval: e.target.checked })
              }),
              span({ class: 'label-text' }, 'Review artwork before mockups')
            ),
            p({ class: 'text-xs opacity-60 ml-1' }, 'Stage 1 gate: approve the raw design in Artwork Approval before spending upscale/mockup credits. Rejections free a slot to refill.')
          ),
          div({ class: 'form-control' },
            label({ class: 'label cursor-pointer justify-start gap-3' },
              input({
                type: 'checkbox',
                class: 'toggle toggle-primary',
                checked: autopilot.bindAttr(a => a.tribe_engagement_weighting),
                onchange: (e) => autopilot.assign({ tribe_engagement_weighting: e.target.checked })
              }),
              span({ class: 'label-text' }, 'Weight tribes by favourites per view')
            ),
            p({ class: 'text-xs opacity-60 ml-1' }, 'Variation seeds pick a tribe by what shoppers favourite on published canvas listings, blended with units sold. Off: units sold alone. Inspect the table with go run ./cmd/tribe-report.')
          ),
          div({ class: 'divider text-xs opacity-60' }, 'Generation learning'),
          p({ class: 'text-xs opacity-60 -mt-2 mb-1' }, 'All off by default. Off, seeds are drawn exactly as before; the two learned switches still compute what they would do and log it for review. Preview the next seeds with go run ./cmd/seed-preview -tenant 1.'),
          genToggle(autopilot, 'gen_explore_floor', 'Exploration floor',
            'A fixed share of seeds drawn uniformly from the whole lucky space, ignoring the corpus, your ratings and anything learned, so generation never collapses onto one look.'),
          div({ class: 'form-control ml-12', 'data-field': 'gen_explore_share' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Exploration share (percent of seeds)')),
            NumberSpinner({ state: percentBindAttr(autopilot, 'gen_explore_share'), min: 5, max: 50 })
          ),
          genToggle(autopilot, 'gen_novelty_slot', 'Novelty slot',
            'One seed a day from the least-covered combination of tribe, style, palette and mood among published and in-flight designs.'),
          genToggle(autopilot, 'gen_trend_seeds', 'Trend seeds',
            'One calendar seed a day built from a Pinterest Trends keyword that passes the wall-art check: seasonal with its peak 60 to 90 days out, or growing fast. Comes out of the calendar share.'),
          genToggle(autopilot, 'gen_winner_seeds', 'Winner seeds (learned)',
            'Keeps a top Pinterest design\'s style, palette, mood and format and asks for a new subject. Only once 20 designs have matured scores; 10 percent of seeds, at most 25. Off: shadow only.'),
          genToggle(autopilot, 'gen_pinterest_weights', 'Pinterest feature weights (learned)',
            'Styles, palettes and moods that win on Pinterest are drawn more often, by at most 2x and within the style family cap; the blend grows with matured pins up to 40 percent. Off: shadow only.'),
          genToggle(autopilot, 'gen_thompson_weights', 'Thompson sampling of the feature weights (learned)',
            'With Pinterest feature weights on, each seed draws every style, palette and mood multiplier from its posterior instead of using the mean, so uncertain and untried values still get tried. Same 2x clamp, blend and family cap. Does nothing while the weights are off. Off: shadow only. Turn on only after the weights have run for a review cycle and rest on 200 matured pins.'),
          div({ class: 'divider text-xs opacity-60' }, 'Commercial spaces and sets of 3'),
          p({ class: 'text-xs opacity-60 -mt-2 mb-1' }, 'Off by default. Calm public wall art for offices, lobbies, waiting rooms and schools: soft abstracts, misty landscapes, botanicals, muted palettes, no text or faces. A set of 3 is three canvas listings (left, centre, right) that Printify fulfils like any canvas, plus their framed siblings. See ai-docs/plan_canvas_sets.md.'),
          div({ class: 'form-control', 'data-field': 'gen_commercial_share' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Commercial spaces share (percent of canvas seeds, 0 is off)')),
            NumberSpinner({ state: percentBindAttr(autopilot, 'gen_commercial_share'), min: 0, max: 50 })
          ),
          genToggle(autopilot, 'gen_triptych', 'Sets of 3 canvases',
            'Some canvas seeds become a coordinated set of three panels, generated as one wide view split in three or as three panels from one continuity spec, checked by a set gate, and listed as three canvases with set scenes in office rooms.'),
          div({ class: 'form-control ml-12', 'data-field': 'gen_triptych_share' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Set share (percent of canvas seeds)')),
            NumberSpinner({ state: percentBindAttr(autopilot, 'gen_triptych_share'), min: 5, max: 50 })
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Product types to generate')),
            p({ class: 'text-xs opacity-60 mb-2' },
              'Which products Autopilot creates. Per-listing rates are the ones to judge on, a type with more listings wins on totals while losing on every listing. Turning a type off stops new generation; listings already published are untouched.'),
            loadingProductTypes.bindAs(l => l
              ? div({ class: 'text-sm opacity-60' }, 'Loading product types…')
              : productTypes.bindChildren(div({ class: 'space-y-2' }), productTypeRow)
            )
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'New products per day')),
            NumberSpinner({ state: wrapBindAttr(autopilot, 'target_listings_per_day'), min: 1, max: 50 })
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Weekly ceiling (max products / 7 days)')),
            NumberSpinner({ state: wrapBindAttr(autopilot, 'max_weekly_credits'), min: 1, max: 500 })
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Check interval (minutes)')),
            NumberSpinner({ state: wrapBindAttr(autopilot, 'tick_interval_minutes'), min: 15, max: 1440 })
          ),
          div({ class: 'divider text-xs opacity-60' }, 'Auto-retire (tombstoning)'),
          p({ class: 'text-xs opacity-60 -mt-2 mb-1' }, 'Only autonomous products are ever retired, never your manual listings, pinned items, or anything with a review. A sale grants another year of life.'),
          div({ class: 'form-control' },
            label({ class: 'label cursor-pointer justify-start gap-3' },
              input({
                type: 'checkbox',
                class: 'toggle toggle-primary',
                checked: autopilot.bindAttr(a => a.tombstone_enabled),
                onchange: (e) => autopilot.assign({ tombstone_enabled: e.target.checked })
              }),
              span({ class: 'label-text' }, 'Enable auto-retire')
            )
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Never drop the shop below (items)')),
            NumberSpinner({ state: wrapBindAttr(autopilot, 'tombstone_min_shop_items'), min: 0, max: 5000 })
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Retire after (days) with no sale + low engagement')),
            NumberSpinner({ state: wrapBindAttr(autopilot, 'tombstone_age_days'), min: 30, max: 365 })
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Life granted per sale (days)')),
            NumberSpinner({ state: wrapBindAttr(autopilot, 'tombstone_sale_ttl_days'), min: 30, max: 1095 })
          )
        ),
        div({ class: 'card-actions justify-end mt-6' },
          button({
            class: 'btn btn-primary',
            disabled: autopilotLoaded.bindAttr(l => !l),
            onclick: saveAutopilot
          },
          savingAutopilot.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save Autopilot Settings')
          )
        )
      ),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-1' }, 'Product Variants & Pricing'),
        p({ class: 'text-sm opacity-70 mb-4' },
          'Per product type: which sizes to enable and the profit margin. Applies to autopilot and manual products. Leave blank to use the built-in defaults.'),
        loadingBlueprints.bindAs(l => l
          ? div({ class: 'flex justify-center p-6' }, span({ class: 'loading loading-spinner' }))
          : blueprints.bindAs(list => (!Array.isArray(list) || list.length === 0)
            ? div({ class: 'text-sm opacity-60' }, 'No product types configured yet.')
            : div({ class: 'space-y-3' }, list.map(blueprintRow)))
        )
      ),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-1' }, 'Etsy'),
        p({ class: 'text-sm opacity-70 mb-4' },
          'Connect your Etsy shop to sync listing stats and (in etsy_direct mode) upload mockups directly to your listings.'),
        etsy.bindAs(e => !e.appConfigured
          ? div({ class: 'alert alert-warning text-sm' },
            span('Etsy isn\'t set up on this platform yet, an administrator needs to configure the Etsy app credentials before you can connect.'))
          : div({ class: 'flex items-center gap-4 flex-wrap' },
            e.hasOAuth
              ? span({ class: 'badge badge-success gap-1' }, '✓ Connected' + (e.shopId ? `: shop ${e.shopId}` : ''))
              : span({ class: 'text-sm opacity-70' }, 'Not connected yet.'),
            button({
              class: 'btn btn-primary btn-sm',
              disabled: savingEtsy.bindAttr(s => s),
              onclick: connectEtsy
            }, savingEtsy.bindAs(s => s ? span({ class: 'loading loading-spinner loading-xs' }) : e.hasOAuth ? 'Reconnect Etsy' : 'Connect Etsy'))
          ))
      ),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-1' }, 'Pinterest'),
        p({ class: 'text-sm opacity-70 mb-4' },
          'Connect your Pinterest business account to pin products and drive traffic to your Etsy listings.'),
        pinterest.bindAs(pt => !pt.appConfigured
          ? div({ class: 'alert alert-warning text-sm' },
            span('Pinterest isn\'t set up on this platform yet, an administrator needs to configure the Pinterest app credentials before you can connect.'))
          : div({ class: 'flex items-center gap-4 flex-wrap' },
            pt.hasOAuth
              ? span({ class: 'badge badge-success gap-1' }, '✓ Connected' + (pt.username ? `: @${pt.username}` : ''))
              : span({ class: 'text-sm opacity-70' }, 'Not connected yet.'),
            button({
              class: 'btn btn-primary btn-sm',
              disabled: savingPinterest.bindAttr(s => s),
              onclick: connectPinterest
            }, savingPinterest.bindAs(s => s ? span({ class: 'loading loading-spinner loading-xs' }) : pt.hasOAuth ? 'Reconnect Pinterest' : 'Connect Pinterest')),
            pt.hasOAuth
              ? button({
                class: 'btn btn-ghost btn-sm',
                disabled: savingPinterest.bindAttr(s => s),
                onclick: disconnectPinterest
              }, 'Disconnect')
              : span()
          )),
        div({ class: 'divider my-2' }, 'Pin Queue'),
        p({ class: 'text-sm opacity-70 mb-2' },
          'Drafts pins of your published canvas listings every day for you to review. Nothing is posted to Pinterest until you approve that pin, here in the ',
          fnlink({ to: '/pin-queue', class: 'link link-primary' }, 'Pin Queue'),
          ' or on its Discord card. Approved pins go out one at a time, a few hours apart.'),
        div({ class: 'flex flex-wrap items-end gap-4' },
          label({ class: 'label cursor-pointer gap-2' },
            input({
              type: 'checkbox',
              name: 'pinterest_autopin_enabled',
              class: 'toggle toggle-primary',
              checked: settings.bindAttr(s => !!s.pinterest_autopin_enabled),
              onchange: (e) => settings.assign({ pinterest_autopin_enabled: e.target.checked })
            }),
            span({ class: 'label-text' }, 'Draft pins for review')
          ),
          label({ class: 'label cursor-pointer gap-2', title: 'Drafts the critic passes are approved automatically and go out at their staggered, jittered slots. The Discord card keeps a Reject button until the pin publishes. Pinterest asks that you choose each pin; this skips that click.' },
            input({
              type: 'checkbox',
              name: 'pinterest_auto_approve',
              class: 'toggle toggle-sm',
              checked: settings.bindAttr(s => !!s.pinterest_auto_approve),
              onchange: (e) => settings.assign({ pinterest_auto_approve: e.target.checked })
            }),
            span({ class: 'label-text' }, 'Auto-approve pins')
          ),
          label({ class: 'label cursor-pointer gap-2', title: 'Gemini writes each pin title, description and alt text, and a critic checks every draft before it reaches you. Off: the listing copy, trimmed.' },
            input({
              type: 'checkbox',
              name: 'pinterest_ai_copy',
              class: 'toggle toggle-sm',
              checked: settings.bindAttr(s => s.pinterest_ai_copy !== false),
              onchange: (e) => settings.assign({ pinterest_ai_copy: e.target.checked })
            }),
            span({ class: 'label-text' }, 'AI copy and critic')
          ),
          label({ class: 'label cursor-pointer gap-2', title: 'Re-pins after the first get a fresh 2:3 pin image (room, caption or stacked layout) instead of the same mockup. Off: the raw mockups.' },
            input({
              type: 'checkbox',
              name: 'pinterest_creatives',
              class: 'toggle toggle-sm',
              checked: settings.bindAttr(s => s.pinterest_creatives !== false),
              onchange: (e) => settings.assign({ pinterest_creatives: e.target.checked })
            }),
            span({ class: 'label-text' }, 'Fresh pin images')
          ),
          label({ class: 'label cursor-pointer gap-2', title: 'The learner picks listings, image types and boards from what matured pins scored (30 percent of drafts still follow the old rules). Off: it only runs in shadow and logs what it would have picked, for review.' },
            input({
              type: 'checkbox',
              name: 'pinterest_learned_selection',
              class: 'toggle toggle-sm',
              checked: settings.bindAttr(s => !!s.pinterest_learned_selection),
              onchange: (e) => settings.assign({ pinterest_learned_selection: e.target.checked })
            }),
            span({ class: 'label-text' }, 'Learned selection')
          ),
          label({ class: 'label cursor-pointer gap-2', title: 'Weekly: propose up to two new boards a month from Pinterest trends, as cards you approve or reject. Nothing changes until you approve one.' },
            input({
              type: 'checkbox',
              name: 'pinterest_board_explore_enabled',
              class: 'toggle toggle-sm',
              checked: settings.bindAttr(s => s.pinterest_board_explore_enabled !== false),
              onchange: (e) => settings.assign({ pinterest_board_explore_enabled: e.target.checked })
            }),
            span({ class: 'label-text' }, 'Propose new boards')
          ),
          label({ class: 'label cursor-pointer gap-2', title: 'At most once a day: when you reject an artwork the market model rates in the top tenth, in features you usually rate below the market, the AI sends one appeal card. You always have the last word. Off: appeals are only recorded, for review.' },
            input({
              type: 'checkbox',
              name: 'taste_challenger_enabled',
              class: 'toggle toggle-sm',
              checked: settings.bindAttr(s => !!s.taste_challenger_enabled),
              onchange: (e) => settings.assign({ taste_challenger_enabled: e.target.checked })
            }),
            span({ class: 'label-text' }, 'AI appeals on rejected art')
          ),
          div({ class: 'form-control w-40' },
            label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Pins per day')),
            input({
              type: 'number',
              name: 'pinterest_daily_pins',
              min: '1',
              max: '20',
              step: '1',
              class: 'input input-bordered input-sm',
              value: settings.bindAttr(s => s.pinterest_daily_pins ?? 8),
              oninput: (e) => {
                const v = parseInt(e.target.value)
                settings.assign({ pinterest_daily_pins: Number.isFinite(v) ? v : 8 })
              }
            })
          ),
          button({
            class: 'btn btn-primary btn-sm',
            disabled: settingsLoaded.bindAttr(l => !l),
            onclick: savePinQueue
          }, savingPinQueue.bindAs(s => s ? span({ class: 'loading loading-spinner loading-xs' }) : 'Save Pin Queue'))
        )
      ),

      // Super-admin only: the app-level Etsy credentials (shared by all tenants).
      user.bindAs(u => (u.tenant_id === 1 && u.role === 'SUPER_ADMIN')
        ? div({ class: 'card bg-base-200 border border-warning/40 p-6' },
          h2({ class: 'text-xl font-bold mb-1' }, 'Etsy App Credentials ',
            span({ class: 'badge badge-warning badge-sm align-middle' }, 'admin')),
          p({ class: 'text-sm opacity-70 mb-4' },
            'App-level Etsy API credentials, shared by all tenants. Keystring = OAuth client_id; Shared Secret = the x-api-key for data requests. From your Etsy developer app.'),
          div({ class: 'space-y-4' },
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text' }, 'App Keystring')),
              input({
                type: 'text',
                class: 'input input-bordered w-full',
                placeholder: 'Etsy app keystring',
                value: etsyApp.bindAttr(a => a.keystring),
                oninput: (ev) => etsyApp.assign({ keystring: ev.target.value })
              })
            ),
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text' }, 'App Shared Secret')),
              input({
                type: 'password',
                class: 'input input-bordered w-full',
                placeholder: etsyApp.bindAttr(a => a.hasSecret ? '•••••• (saved: enter to replace)' : 'Etsy app shared secret'),
                value: etsyApp.bindAttr(a => a.sharedSecret),
                oninput: (ev) => etsyApp.assign({ sharedSecret: ev.target.value })
              })
            )
          ),
          div({ class: 'card-actions justify-end mt-6' },
            button({ class: 'btn btn-primary', disabled: savingEtsyApp.bindAttr(s => s), onclick: saveEtsyApp },
              savingEtsyApp.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save App Credentials'))
          )
        )
        : span()),

      user.bindAs(u => (u.tenant_id === 1 && u.role === 'SUPER_ADMIN')
        ? div({ class: 'card bg-base-200 border border-warning/40 p-6' },
          h2({ class: 'text-xl font-bold mb-1' }, 'Pinterest App Credentials ',
            span({ class: 'badge badge-warning badge-sm align-middle' }, 'admin')),
          p({ class: 'text-sm opacity-70 mb-4' },
            'App-level Pinterest API credentials from your Pinterest developer app, shared by all tenants. App ID = OAuth client_id; App Secret = client_secret.'),
          div({ class: 'space-y-4' },
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text' }, 'App ID (client_id)')),
              input({
                type: 'text',
                class: 'input input-bordered w-full',
                placeholder: 'Pinterest app id',
                value: pinterestApp.bindAttr(a => a.clientId),
                oninput: (ev) => pinterestApp.assign({ clientId: ev.target.value })
              })
            ),
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text' }, 'App Secret (client_secret)')),
              input({
                type: 'password',
                class: 'input input-bordered w-full',
                placeholder: pinterestApp.bindAttr(a => a.hasSecret ? '•••••• (saved: enter to replace)' : 'Pinterest app secret'),
                value: pinterestApp.bindAttr(a => a.clientSecret),
                oninput: (ev) => pinterestApp.assign({ clientSecret: ev.target.value })
              })
            )
          ),
          div({ class: 'card-actions justify-end mt-6' },
            button({ class: 'btn btn-primary', disabled: savingPinterestApp.bindAttr(s => s), onclick: savePinterestApp },
              savingPinterestApp.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save App Credentials'))
          )
        )
        : span()),

      user.bindAs(u => (u.tenant_id === 1 && u.role === 'SUPER_ADMIN')
        ? div({ class: 'card bg-base-200 border border-warning/40 p-6' },
          h2({ class: 'text-xl font-bold mb-1' }, 'BFL Image Credits ',
            span({ class: 'badge badge-warning badge-sm align-middle' }, 'admin')),
          p({ class: 'text-sm opacity-70 mb-4' },
            'Get a Discord alert when your Black Forest Labs (Flux) balance drops below this threshold, so you can top up before autopilot image + mockup generation stalls. Set to 0 to disable.'),
          div({ class: 'mb-4 text-sm' },
            span({ class: 'opacity-70' }, 'Current balance: '),
            bflApp.bindAs(a => a.credits === null || a.credits === undefined
              ? span({ class: 'opacity-50' }, 'unavailable')
              : span({ class: 'font-bold' }, `${Math.round(a.credits)} credits`))
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Low-credit alert threshold')),
            input({
              type: 'number',
              min: '0',
              class: 'input input-bordered w-full',
              placeholder: '0 = disabled',
              value: bflApp.bindAttr(a => a.lowCreditThreshold),
              oninput: (ev) => bflApp.assign({ lowCreditThreshold: ev.target.value })
            })
          ),
          div({ class: 'card-actions justify-end mt-6' },
            button({ class: 'btn btn-primary', disabled: savingBflApp.bindAttr(s => s), onclick: saveBflApp },
              savingBflApp.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save Threshold'))
          )
        )
        : span()),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-4' }, 'Discord Notifications'),
        div({ class: 'space-y-4' },
          div({ class: 'text-sm opacity-80' },
            discord.bindAs(d => d.hasWebhook
              ? span({ class: 'text-success font-semibold' }, '✓ Connected')
              : span('Get alerts when your Printify session expires so you can re-login before autopilot pauses. In Discord: Server Settings → Integrations → Webhooks → New Webhook → Copy URL.'))
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Discord Webhook URL')),
            input({
              type: 'password',
              class: 'input input-bordered w-full',
              placeholder: 'https://discord.com/api/webhooks/…',
              value: discord.bindAttr(d => d.webhookUrl),
              oninput: (e) => discord.assign({ webhookUrl: e.target.value })
            })
          )
        ),
        div({ class: 'card-actions justify-end mt-6 gap-2' },
          button({
            class: 'btn btn-ghost',
            disabled: savingDiscord.bindAttr(s => s),
            onclick: sendTestDiscord
          }, 'Send Test Notification'),
          button({
            class: 'btn btn-primary',
            disabled: savingDiscord.bindAttr(s => s),
            onclick: saveDiscord
          },
          savingDiscord.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save Webhook')
          )
        )
      ),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-4' }, 'Browser Extension'),
        div({ class: 'space-y-4' },
          div({ class: 'alert alert-info text-sm' },
            span({ class: 'material-symbols-rounded' }, 'info'),
            div(
              span({ class: 'font-semibold' }, 'Manual installation required. '),
              'Download the extension, unzip it, then go to ',
              span({ class: 'font-mono text-xs' }, 'chrome://extensions'),
              ' → Enable "Developer mode" → "Load unpacked" → select the unzipped folder.'
            )
          ),
          div({ class: 'flex items-center gap-3' },
            a({
              href: '/downloads/etherbuildr-chrome-extension.zip',
              download: 'etherbuildr-chrome-extension.zip',
              class: 'btn btn-primary gap-2'
            },
            span({ class: 'material-symbols-rounded' }, 'download'),
            'Download Extension'
            )
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Extension ID')),
            input({
              type: 'text',
              class: 'input input-bordered',
              placeholder: 'Enter your Chrome Extension ID',
              value: extensionId.bindAttr(),
              oninput: (e) => {
                const id = e.target.value.trim()
                extensionId(id)
                localStorage.setItem('etherbuildr_extension_id', id)
              }
            }),
            label({ class: 'label' },
              span({ class: 'label-text-alt opacity-50' }, 'Found in chrome://extensions after loading the unpacked extension.')
            )
          )
        )
      ),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-4' }, 'Pricing Strategy'),
        div({ class: 'space-y-4' },
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Strategy')),
            select({
              class: 'select select-bordered w-full',
              onchange: (e) => settings.assign({ pricing_strategy: e.target.value })
            },
            option({
              value: 'TARGET_PROFIT',
              selected: settings.bindAttr(s => s.pricing_strategy === 'TARGET_PROFIT')
            }, 'Target Profit ($ per item)'),
            option({
              value: 'FIXED_MARGIN',
              selected: settings.bindAttr(s => s.pricing_strategy === 'FIXED_MARGIN')
            }, 'Fixed Margin (% of revenue)')
            )
          ),
          settings.bindAs(s => s.pricing_strategy === 'TARGET_PROFIT'
            ? div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Target Profit (Cents)')),
              input({
                type: 'number',
                class: 'input input-bordered',
                value: s.target_profit,
                oninput: (e) => settings.assign({ target_profit: parseInt(e.target.value) })
              }),
              label({ class: 'label' }, span({ class: 'label-text-alt opacity-50' }, `Each listing will be priced at Cost + $${(s.target_profit / 100).toFixed(2)}`))
            )
            : div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Target Margin (%)')),
              input({
                type: 'number',
                step: '0.01',
                class: 'input input-bordered',
                value: s.target_margin,
                oninput: (e) => settings.assign({ target_margin: parseFloat(e.target.value) })
              }),
              label({ class: 'label' }, span({ class: 'label-text-alt opacity-50' }, `Each listing will target a ${(s.target_margin * 100).toFixed(0)}% net margin.`)),
              div({ class: 'grid grid-cols-2 gap-4 mt-2' },
                div({ class: 'form-control' },
                  label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Large Size Margin (fraction)')),
                  input({
                    type: 'number',
                    step: '0.01',
                    min: '0.01',
                    max: '0.99',
                    placeholder: 'none',
                    class: 'input input-bordered',
                    value: s.large_size_margin ?? '',
                    oninput: (e) => {
                      const v = parseFloat(e.target.value)
                      settings.assign({ large_size_margin: Number.isFinite(v) ? v : null })
                    }
                  })
                ),
                div({ class: 'form-control' },
                  label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Large Size Min Short Side (in)')),
                  input({
                    type: 'number',
                    step: '1',
                    min: '1',
                    placeholder: 'none',
                    class: 'input input-bordered',
                    value: s.large_size_min_side_in ?? '',
                    oninput: (e) => {
                      const v = parseInt(e.target.value)
                      settings.assign({ large_size_min_side_in: Number.isFinite(v) ? v : null })
                    }
                  })
                )
              ),
              label({ class: 'label' }, span({ class: 'label-text-alt opacity-50' },
                (s.large_size_margin > 0 && s.large_size_min_side_in > 0)
                  ? `Sizes whose shorter side is ${s.large_size_min_side_in}" or more target a ${(s.large_size_margin * 100).toFixed(0)}% margin instead. Blueprint pricing overrides still win.`
                  : 'Set both to price large sizes (for example 18" x 24" and up) at a lower margin. Leave empty for one margin on every size.'))
            )
          ),
          div({ class: 'divider my-2' }, 'Etsy sale'),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Etsy Sale Percent')),
            input({
              type: 'number',
              name: 'etsy_sale_percent',
              step: '1',
              min: '0',
              max: '70',
              class: 'input input-bordered',
              value: settings.bindAttr(s => s.etsy_sale_percent ?? 0),
              oninput: (e) => {
                const v = parseInt(e.target.value)
                settings.assign({ etsy_sale_percent: Number.isFinite(v) ? v : 0 })
              }
            }),
            label({ class: 'label' }, span({ class: 'label-text-alt opacity-50' },
              'The permanent shop-wide sale that list prices are grossed up for. Change it together with the sale in Etsy, or the shop is mispriced by the gap.'))
          ),
          p({ class: 'text-xs opacity-60' }, 'Etsy has no API for sales; record the sale you created here so the reminder resets'),
          div({ class: 'grid grid-cols-2 gap-4' },
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Sale Name')),
              input({
                type: 'text',
                name: 'etsy_sale_name',
                placeholder: 'SALE-NOV-26',
                class: 'input input-bordered',
                value: settings.bindAttr(s => s.etsy_sale_name ?? ''),
                oninput: (e) => settings.assign({ etsy_sale_name: e.target.value || null })
              })
            ),
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Applies To')),
              select({
                name: 'etsy_sale_scope',
                class: 'select select-bordered w-full',
                onchange: (e) => settings.assign({ etsy_sale_scope: e.target.value || null })
              },
              option({ value: '', selected: settings.bindAttr(s => !s.etsy_sale_scope) }, 'Not recorded'),
              option({ value: 'all_listings', selected: settings.bindAttr(s => s.etsy_sale_scope === 'all_listings') }, 'All listings'),
              option({ value: 'listing_selected', selected: settings.bindAttr(s => s.etsy_sale_scope === 'listing_selected') }, 'Selected listings (new listings not covered)')
              )
            ),
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Starts')),
              input({
                type: 'date',
                name: 'etsy_sale_starts_at',
                class: 'input input-bordered',
                value: settings.bindAttr(s => s.etsy_sale_starts_at ?? ''),
                oninput: (e) => settings.assign({ etsy_sale_starts_at: e.target.value || null })
              })
            ),
            div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Ends (30 days at most)')),
              input({
                type: 'date',
                name: 'etsy_sale_ends_at',
                class: 'input input-bordered',
                value: settings.bindAttr(s => s.etsy_sale_ends_at ?? ''),
                oninput: (e) => settings.assign({ etsy_sale_ends_at: e.target.value || null })
              })
            )
          )
        ),
        div({ class: 'card-actions justify-end mt-6' },
          button({
            class: 'btn btn-primary',
            disabled: saving.bindAttr(s => s),
            onclick: saveSettings
          },
          saving.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save Strategy')
          )
        )
      ),

      div({ class: 'card bg-base-200 border border-base-300 p-6' },
        h2({ class: 'text-xl font-bold mb-4' }, 'Printify Integration'),
        div({ class: 'space-y-4' },
          div({ class: 'form-control' },
            label({ class: 'label' },
              span({ class: 'label-text' }, 'API Access Token'),
              printify.bindAs(p => p.hasToken ? span({ class: 'badge badge-success badge-sm' }, 'Token Set') : '')
            ),
            input({
              type: 'password',
              class: 'input input-bordered',
              placeholder: printify.bindAttr(p => p.hasToken ? '••••••••••••••••' : 'Enter your Printify API Token'),
              value: printify.bindAttr(p => p.accessToken),
              oninput: handleTokenInput
            }),
            label({ class: 'label' },
              span({ class: 'label-text-alt opacity-50' }, 'Tokens are stored using secure encryption.')
            )
          ),
          printify.bindAs(p => (p.hasToken || p.accessToken)
            ? div({ class: 'form-control' },
              label({ class: 'label' }, span({ class: 'label-text' }, 'Shop ID')),
              select({
                class: 'select select-bordered w-full',
                onchange: async (e) => {
                  const current = printify().shopId
                  const next = e.target.value
                  if (current && next !== current) {
                    const ok = await window.confirm(
                      'Are you sure you want to change your Printify shop? Everything EtherBuildr has pushed or published is tied to the current shop, switching will break re-pushes, publishing, and syncing for those products.',
                      'Change Printify Shop?'
                    )
                    if (!ok) {
                      e.target.value = current
                      return
                    }
                  }
                  printify.assign({ shopId: next })
                  savePrintify(true)
                }
              },
              option({ value: '', disabled: true, selected: !p.shopId }, 'Select a shop'),
              p.shops.map(s => option({
                value: s.id.toString(),
                selected: p.shopId === s.id.toString()
              }, s.title + (s.sales_channel === 'disconnected' ? ' (disconnected)' : '')))
              )
            )
            : div()
          ),

          div({ class: 'divider my-2' }),

          div({ class: 'form-control' },
            label({ class: 'label' },
              span({ class: 'label-text' }, 'Printify Login'),
              printify.bindAs(p => p.hasSessionToken
                ? span({ class: 'badge badge-success badge-sm' }, 'Connected')
                : span({ class: 'badge badge-warning badge-sm' }, 'Not Connected')
              )
            ),
            div({ class: 'space-y-3' },
              div(printify.bindAs(p => {
                const s = sessionStatus(p)
                return span({ class: 'text-sm ' + s.cls }, s.text)
              })),
              p({ class: 'text-sm opacity-70' },
                'Sign in to Printify in a secure server-side browser, used by autopilot and manual publishing, no extension needed. Reconnect every day or two; we\'ll ping Discord when the session is about to expire.'),
              div({ class: 'flex justify-end' },
                button({
                  class: 'btn btn-primary',
                  onclick: () => goTo('/integrations/connect/printify')
                }, printify.bindAs(p => p.hasSessionToken ? 'Reconnect Printify' : 'Connect Printify'))
              )
            )
          )
        ),
        div({ class: 'card-actions justify-end mt-6' },
          button({
            class: 'btn btn-primary',
            disabled: savingPrintify.bindAttr(s => s),
            onclick: () => savePrintify()
          },
          savingPrintify.bindAs(s => s ? span({ class: 'loading loading-spinner' }) : 'Save Integration')
          )
        )
      )
    )
  )
}

// genToggle is one generation-learning switch with its explanation.
function genToggle (state, prop, title, help) {
  return div({ class: 'form-control' },
    label({ class: 'label cursor-pointer justify-start gap-3' },
      input({
        type: 'checkbox',
        name: prop,
        class: 'toggle toggle-primary',
        checked: state.bindAttr(a => !!a[prop]),
        onchange: (e) => state.assign({ [prop]: e.target.checked })
      }),
      span({ class: 'label-text' }, title)
    ),
    p({ class: 'text-xs opacity-60 ml-1' }, help)
  )
}

// percentBindAttr exposes a 0..1 share on an object state as a whole percent
// for NumberSpinner.
function percentBindAttr (state, prop) {
  const toPct = s => Math.round((Number(s[prop]) || 0) * 100)
  const wrapper = function (pct) {
    if (arguments.length === 0) return toPct(state())
    return state.assign({ [prop]: pct / 100 })
  }
  Object.assign(wrapper, state)
  wrapper.bindAttr = function () {
    return state.bindAttr(toPct)
  }
  return wrapper
}

function wrapBindAttr (state, prop) {
  const wrapper = function (newNumber) {
    if (arguments.length === 0) return state()[prop]
    return state.assign({ [prop]: newNumber })
  }
  Object.assign(wrapper, state)
  wrapper.bindAttr = function () {
    return state.bindAttr(s => s[prop])
  }
  return wrapper
}
